import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { Sun, Moon, SmartphoneNfc } from 'lucide-react';

const ThemeContext = createContext();

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const applyTheme = (t) => {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = t === 'dark' || (t === 'auto' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
  return isDark;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem('theme') || 'auto'; } catch { return 'auto'; }
  });

  useEffect(() => {
    applyTheme(theme);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => { if (theme === 'auto') applyTheme('auto'); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((t) => {
    try { localStorage.setItem('theme', t); } catch { /* ignore */ }
    setThemeState(t);
    applyTheme(t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const options = [
    { key: 'auto', label: 'Auto', icon: SmartphoneNfc },
    { key: 'light', label: 'Light', icon: Sun },
    { key: 'dark', label: 'Dark', icon: Moon },
  ];
  return (
    <div className="inline-flex w-full sm:w-auto p-1 rounded-2xl bg-secondary" data-testid="theme-toggle">
      {options.map((o) => {
        const active = theme === o.key;
        return (
          <button
            key={o.key}
            onClick={() => setTheme(o.key)}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all active:scale-95 ${
              active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`theme-option-${o.key}`}
          >
            <o.icon size={15} strokeWidth={1.75} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
};
